import { useSelector } from "react-redux";
import { BASE_USER, ADMINISTRATOR, MANAGER, MANAGER_PLUS, RESTRICTED_CONTRIBUTOR, CONTRIBUTOR, BASE_CHECKER, SUPERVISOR, CONTRIBUTOR_PLUS } from "@/constants/roles";

const NO_ITEMS = [];

export const useNavigationItems = () => {
    const maxUserRole = useSelector(state => state.userDetails.maxUserRole);
    const permissions = useSelector(state => state.userDetails.permissions || NO_ITEMS);
    const accessToAllSitesDashboard = useSelector(state => state.shared.accessToAllSitesDashboard);
    const currentSiteId = useSelector(state => state.shared.siteId);
    const lastSiteId = useSelector(state => state.shared.lastSiteId);
    const sites = useSelector(state => state.dashboardPage.sites);
    const accountDashboardSettings = useSelector(state => state.dashboardPage.accountDashboardSettings);

    const haveAccessToAllSitesDashboard = accessToAllSitesDashboard && permissions.length > 1;

    if (!maxUserRole) { return []; }

    const dashboardSettingsSiteId = currentSiteId != null && String(currentSiteId) !== "0"
        ? currentSiteId
        : lastSiteId;
    const currentAccountId = dashboardSettingsSiteId && sites.length > 0
        ? sites.find(site => String(site.siteId) === String(dashboardSettingsSiteId))?.accountId
        : null;

    const accountSettings = currentAccountId ? accountDashboardSettings[currentAccountId] : null;

    const distributionDashboards = [
        { title: "Critical Equipment", url: "/distribution-dashboard/0", icon: "ri-plug-line", textColor: '#6FD555', key: "criticalEquipmentDashboard", recordTypeId: 0 },
        { title: "Standard Jobs", url: "/distribution-dashboard/10", icon: "ri-suitcase-line", textColor: '#5F98EC', key: "standardJobsDashboard", recordTypeId: 10 },
        { title: "Books", url: "/distribution-dashboard/20", icon: "ri-book-line", textColor: '#EAC54E', key: "booksDashboard", recordTypeId: 20 },
        { title: "Isolations", url: "/distribution-dashboard/30", icon: "ri-lock-unlock-line", textColor: '#EE7F4F', key: "isolationsDashboard", recordTypeId: 30 },
        { title: "Safety", url: "/distribution-dashboard/40", icon: "ri-shield-check-line", textColor: '#4BC7BF', key: "safetyDashboard", recordTypeId: 40 }
    ];

    let activeDashboards = distributionDashboards;

    if (accountSettings) {
        activeDashboards = distributionDashboards
            .map(dashboard => {
                const setting = accountSettings.find(s => s.cerRecordTypeId === dashboard.recordTypeId);
                if (!setting) return dashboard;
                return {
                    ...dashboard,
                    title: setting.dashboardName || dashboard.title,
                    icon: setting.icon || dashboard.icon,
                    _sortOrder: setting.sortOrder,
                    _active: setting.active,
                    _totalItems: setting.totalItems,
                };
            })
            .filter(d => d._active !== false)
            .sort((a, b) => (a._sortOrder ?? 999) - (b._sortOrder ?? 999));
    }

    const dashboardItems = [
        { title: "Main Dashboard", url: "/", icon: "ri-draft-line", key: "documentsDashboard" },
        ...activeDashboards,
    ];

    if (haveAccessToAllSitesDashboard) {
        const allSitesDashboard ={ title: "All Sites", url: "/all-sites-dashboard", icon: "ri-earth-line", textColor: '#C981C9', key: "allSitesDashboard" };
        dashboardItems.splice(1, 0, allSitesDashboard);
    }

    return [
        {
            title: "Dashboards", iconType: "desktop", url: "/", key: "dashboards",
            items: dashboardItems
        },
        {
            title: "Summaries", url: "/summaries", iconType: "file-done", key: "summaries",
            items:
                [
                    [ADMINISTRATOR, MANAGER, MANAGER_PLUS, SUPERVISOR, CONTRIBUTOR, RESTRICTED_CONTRIBUTOR, CONTRIBUTOR_PLUS].includes(maxUserRole) && { title: "Approval", url: "/approval-summary", icon: "ri-chat-check-line", key: "approvalSummary" },
                    [ADMINISTRATOR, MANAGER, MANAGER_PLUS, SUPERVISOR, CONTRIBUTOR, BASE_CHECKER, RESTRICTED_CONTRIBUTOR, CONTRIBUTOR_PLUS].includes(maxUserRole) && { title: "Checking", url: "/checking-summary", icon: "ri-chat-history-line", key: "checkingSummary" },
                    { title: "Jobs", url: "/job-summary", icon: "ri-suitcase-line", key: "jobsSummary" }
                ].filter(Boolean)
        },
        ![BASE_USER, BASE_CHECKER].includes(maxUserRole) &&
        {
            title: "Utilities", url: "/utilities", key: "utilities",
            items:
                [
                    {
                        title: "Export Metadata", url: "/export-metadata", icon: "ri-upload-line", key: "export",
                    },
                    [MANAGER_PLUS, SUPERVISOR, ADMINISTRATOR].includes(maxUserRole) &&
                    {
                        title: "Import Metadata", url: "/import-metadata", icon: "ri-download-line", key: "import",
                    },
                    maxUserRole === ADMINISTRATOR &&
                    { title: "Check For Locked PDFs", url: "/check-for-locked-pdfs", icon: "ri-file-shield-2-line", key: "lockedPDF" },
                    maxUserRole === ADMINISTRATOR &&
                    { title: "Check Files", url: "/check-files", icon: "ri-file-search-line", key: "checkFiles" },
                    { title: "Batch File Rename Documents", url: "/batch-file-rename", icon: "ri-font-size", key: "documentRename" },
                    [ADMINISTRATOR, MANAGER_PLUS, MANAGER, SUPERVISOR].includes(maxUserRole) &&
                    { title: "Deleted Documents", url: "/deleted-documents", icon: "ri-delete-bin-4-line", key: "deletedDocuments" },
                    { title: "Doc/Dwg No. Check", url: "/document-number-availability-check", icon: "ri-hashtag", key: "docNumberAvailable" },
                    { title: "Doc/Dwg No. Generator", url: "/document-number-generator", icon: "ri-file-add-line", key: "docNumberGenerator" },
                    [ADMINISTRATOR, MANAGER_PLUS, MANAGER, SUPERVISOR].includes(maxUserRole) &&
                    {
                        title: "Doc/Dwg No. Generator Setup",
                        url: "/document-numbers",
                        icon: "ri-file-text-line", 
                        key: "documentNumbers",
                        items: [
                            { title: "Document Number System Setup", url: "/document-number-system-setup", icon: "ri-file-settings-line", key: "docNumberSystem"  },
                            { title: "Document Number Components", url: "/document-number-components", icon: "ri-file-chart-line", key: "docNumberComponent" },
                        ]
                    },
                ].filter(Boolean)
        },
        [ADMINISTRATOR, MANAGER, MANAGER_PLUS, SUPERVISOR, CONTRIBUTOR, RESTRICTED_CONTRIBUTOR, CONTRIBUTOR_PLUS].includes(maxUserRole) &&
        {
            title: "Reports", iconType: "area-chart", key: "reports", url: "/reports",
            items:
                [
                    { url: "/reports/siteUserAccess", title: 'Site User Permission Report', icon: "ri-lock-unlock-line", key: "siteUserAccess" },
                    { url: "/reports/underChange", title: 'Documents Under Change', icon: "ri-chat-4-line", key: "underChange" },
                    { url: "/reports/documentsDownloaded", title: 'Downloaded Documents Report', icon: "ri-download-line", key: "documentsDownloaded" },
                    { url: "/reports/documentsUploaded", title: 'Uploaded Documents Report', icon: "ri-upload-line", key: "documentsUploaded" },
                    { url: "/reports/documentsApproved", title: 'Approved Documents Report', icon: "ri-chat-check-line", key: "documentsApproved" },
                    { url: "/reports/loginDetails", title: 'Login History Report', icon: "ri-login-circle-line", key: "loginDetails" }
                ]
        },
        [ADMINISTRATOR, MANAGER, MANAGER_PLUS, SUPERVISOR].includes(maxUserRole) &&
        {
            title: "Management", url: "/management", iconType: "setting", key: "management",
            items:
                [
                    maxUserRole === ADMINISTRATOR && { title: "Administrators", url: "/staff", icon: "ri-team-line", key: "admin" },
                    { title: "Accounts", url: "/accounts", icon: "ri-parent-line", key: "accounts" },
                    maxUserRole === ADMINISTRATOR &&
                    { title: "Secret Migration", url: "/secret-migration", icon: "ri-shield-keyhole-line", key: "secretMigration" },
                ].filter(Boolean)
        },
        {
            title: "About", url: "/about", iconType: "info-circle", key: "about",
            items:
                [
                   { title: "Release Notes", url: "/release-notes", icon: "ri-file-list-3-line", key: "releaseNotes" },
                   { title: "Roadmap", url: "/roadmap", icon: "ri-road-map-line", key: "roadmap" },
                   { title: "Contact Support", url: "/contact-support", icon: "ri-customer-service-2-line", key: "contactSupport" },
                   { title: "Suggest an Improvement", url: "/suggest-improvement", icon: "ri-lightbulb-line", key: "suggestImprovement" },
                   { title: "About Drawing Managers", url: "/about-drawing-managers", icon: "ri-building-line", key: "aboutDrawingManagers" },
                   { title: "Privacy Policy", url: "/privacy-policy", icon: "ri-shield-keyhole-line", key: "privacyPolicy" },
                   { title: "Terms of Use", url: "/terms-of-use", icon: "ri-file-shield-line", key: "termsOfUse" },
                ]
        },
    ].filter(Boolean);
};

export const userProfileDropdownItems = [
    { key: "myProfile", title: "My Profile" },
    { key: "signOut", title: "Sign Out " }
];

export const MAIN_DASHBOARD_NAV_ITEM = {
    title: "Main Dashboard",
    url: "/",
    icon: "ri-draft-line",
    key: "documentsDashboard",
};

// The summary pages surface a Main Dashboard tab as a shortcut back to "/",
// as an alternative to the top navigation "Dashboards" menu. It is added here
// rather than to the Summaries navigation group so that it stays out of the
// top navigation's Summaries dropdown.
export const withMainDashboardTab = (summariesGroup) => {
    if (!summariesGroup) {
        return summariesGroup;
    }

    return {
        ...summariesGroup,
        items: [MAIN_DASHBOARD_NAV_ITEM, ...(summariesGroup.items ?? [])],
    };
};
